import React, { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import Container from '@material-ui/core/Container'
import Accordion from '@material-ui/core/Accordion'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Typography from '@material-ui/core/Typography'
import { JsonLd } from '@components/atoms'
import { RenderRichText } from '@components/renderers/index'
import ButtonCta from '@components/atoms/button-cta'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import useStyles from './accordion-list.styles'

const AccordionList = (props) => {
  const {
    headline,
    itemsCollection,
    centered,
  } = props

  const styles = useStyles()
  const faqRef = useRef(null)

  const [expanded, setExpanded] = useState(0)
  const router = useRouter()
  const targetItemParam = router?.query?.openID

  const handleChange = panel => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false)
  }

  const isMobile = useMediaQuery(useTheme().breakpoints.down('xs'))
  const desktopOffset = !isMobile ? -100 : 0

  useEffect(() => {
    if (targetItemParam && faqRef.current) {
      setTimeout(() => {
        const desiredPosition = faqRef.current.getBoundingClientRect().top
          + window.scrollY + desktopOffset
        window.scrollTo({ top: desiredPosition, behavior: 'smooth' })
      }, 200)
      const targetIndex = itemsCollection?.items?.findIndex(item => item?.id === targetItemParam)
      if (targetIndex !== -1) {
        setExpanded(targetIndex)
      }
    }
  }, [isMobile, targetItemParam, itemsCollection?.items, desktopOffset])

  return (
    <Container ref={faqRef} className={styles.root} data-testid="accordion-list-faq">
      <Typography className={styles.headline} variant="h2" gutterBottom style={{ textAlign: centered ? 'center' : 'left' }}>
        {headline}
      </Typography>
      {itemsCollection?.items?.map((item, i) => (
        <Accordion
          key={item.summary}
          className={styles.container}
          expanded={expanded === i}
          onChange={handleChange(i)}
          id={item.id}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={styles.summary}
          >
            {item.summary}
          </AccordionSummary>
          <AccordionDetails className={styles.details}>
            {item.details
              ? <Typography variant="body1">{item.details}</Typography>
              : <RenderRichText {...item.text} />}
            {item.linksCollection?.items?.length > 0 && item.linksCollection.items.map(link => (
              <ButtonCta
                variant="text"
                arrow
                externalLink={link.ctaLink}
                className={styles.ctaButton}
                key={link.ctaText}
              >
                {link.ctaText}
              </ButtonCta>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
      <JsonLd {...{
        '@type': 'FAQPage',
        mainEntity:
          (itemsCollection?.items || []).map(item => (
            {
              '@type': 'Question',
              name: item.summary,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.details,
              },
            })),
      }}
      />
    </Container>
  )
}

AccordionList.propTypes = {
  headline: PropTypes.string,
  centered: PropTypes.bool,
  itemsCollection: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      summary: PropTypes.string,
      details: PropTypes.string,
    })),
  }),
}

export default AccordionList
